import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteLabel as deleteLabelRequest } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import { workspaceKeys } from '@/features/workspaces';

export interface DeleteLabelInput {
  workspaceId: string;
  labelId: string;
}

/**
 * Remove a label from the workspace's vocabulary.
 *
 * Deleting detaches it from every task carrying it, which is why the surface
 * that calls this confirms with the usage count first. Invalidating the whole
 * aggregate is the right answer for the same reason: the write touches an
 * unbounded set of tasks, so there is no patch that could stand in for a fresh
 * read.
 *
 * Not optimistic. An optimistic delete would take chips off cards across the
 * board before the service had agreed, and putting them back on a failure
 * would look exactly like someone else re-adding them.
 */
export function useDeleteLabel() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteLabelInput>({
    mutationFn: async ({ workspaceId, labelId }) => {
      expectStatus(
        await deleteLabelRequest(workspaceId, labelId),
        204,
        'The label could not be deleted.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
